// NPM Imports
import React from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@revvsales/components";
import { cloneDeep } from "lodash";

// Components Imports
import DocumentBlocks from "../DocumentBlocks";
import { DOCUMENTS } from "../../../constants";
import ContentLock from "../../DocumentItems/ContentLock";

// Actions Imports
import EditorActions from "../../../actions/EditorActions";
import { updateLayoutInsideComponent } from "../../../actions/DocumentActions";

// Internal Imports
import { DOCUMENT_BLOCKS, ADD_COMPONENT_ALLOWED } from "../constants";
import {
  emptyCoverpage,
  emptyContainer,
  cloneComponent,
} from "../../../utils/documentUtils";
import { QuotationActions } from "../../../actions";

// Command Imports
import ToggleComponentLockCommand from "../../../commands/toggleComponentLockCommand";
import ReOrderComponentInCoverPageCommand from "../../../commands/reOrderComponentInCoverPageCommand";

// Destructuring
const { nukeEditorState } = EditorActions;
const { updateToolPaneData } = QuotationActions;
const {
  DOCUMENT_MODE,
  DOCUMENT_COMPONENT_TYPES,
  COMPONENT_FEATURE,
} = DOCUMENTS;

class CoverpageView extends React.Component {
  _onPlaceholderDrop = (rowIndex, colIndex, newRow, dbRowIndex, dbColIndex) => {
    const {
      draggedSection,
      draggedFrom,
      draggedRowIndex,
      draggedColumnIndex,
    } = this.props;
    if (draggedFrom === "new-component") {
      this._addComponentInCoverpage(
        this.props.draggedSection,
        rowIndex,
        colIndex,
        newRow,
        dbRowIndex,
        dbColIndex,
      );
      this.props.clearDragDropData();
    } else {
      const { layout, component } = this.props;
      const reOrderCommand = new ReOrderComponentInCoverPageCommand({
        mode: this.props.mode,
        layout: cloneDeep(layout),
        newRow,
        rowIndex,
        colIndex,
        draggedSection,
        updateComponent: this.props.updateComponent,
        component: cloneDeep(component),
        components: cloneDeep(this.props.components),
        draggedRowIndex,
        draggedColumnIndex,
        draggedFrom,
        draggedTo: "CoverPage",
        updateDraggedSection: this.props.updateDraggedSection,
        updateLayoutInsideComponent: this.props.updateLayoutInsideComponent,
      });
      this.props.executeCommand(reOrderCommand);
    }
  };

  _deleteCoverpageComponent = () => {
    if (this.props.componentId) {
      this.props.deleteComponent(
        this.props.componentId,
        this.props.component.content,
      );
      // this.props.coverpageThemeAction(null);
    }
  };

  _addCoverpage = () => {
    if (this.props.component) {
      if (this.props.component && this.props.component.content) {
        const coverpage = this.props.component.content;
        coverpage.data = {
          ...coverpage.data,
          attributes: {
            ...coverpage.data.attributes,
            isOpen: true,
          },
        };
        this._updateComponentInCoverpage(
          coverpage,
          undefined,
          false,
          this.props.component.content,
        );
      }
    } else {
      const newSection = emptyCoverpage(this.props.mode);
      this.props.addComponent(newSection, 0, 0, true, 0, 0);
    }
    this.props.coverpageThemeAction("OpenThemePane");
  };

  _showCoverpage = () => {
    const {
      content: { data },
    } = this.props.component;
    // if coverpage data has key attributes and that key has a key isOpen
    // which is there for only new coverpage and unavailable for old ones
    // for old coverpages, be default show then
    const showCoverpage =
      data && data.attributes && data.attributes.isOpen !== undefined
        ? data.attributes.isOpen
        : true;
    return showCoverpage;
  };

  _addComponentInCoverpage = (component, rowIndex, colIndex, newRow) => {
    if (component) {
      const { layout, components } = this.props;
      if (newRow) {
        layout.splice(rowIndex, 0, [component.id]);
      } else if (!newRow) {
        const rowIDs = [...layout[rowIndex]];
        rowIDs.splice(colIndex, 0, component.id);
        layout[rowIndex] = rowIDs;
      }
      let { component: coverpageComponent } = this.props;
      const oldCoverpageComponent = { ...coverpageComponent };
      components[component.id] = { content: component };
      coverpageComponent = {
        ...coverpageComponent.content,
        data: {
          ...coverpageComponent.content.data,
          layout,
          components,
        },
      };
      this.props.updateComponent(
        coverpageComponent,
        true,
        false,
        { ...this.props.component, oldCoverpageComponent },
        component,
      );
    }
  };

  _cloneComponentInCoverpage = (oldComponent, rowIndex, colIndex, newRow) => {
    const clonedComponent = cloneComponent(oldComponent, this.props.mode);
    this._addComponentInCoverpage(
      clonedComponent,
      rowIndex + 1,
      colIndex,
      newRow,
    );
  };

  _deleteComponentInCoverpage = (componentID, component) => {
    const { layout, components } = this.props;

    let { component: coverpageComponent } = this.props;

    const oldCoverpageComponent = { ...coverpageComponent };
    const newLayout = layout
      .map(row => row.filter(id => id !== componentID))
      .filter(row => row.length > 0);
    delete components[componentID];

    coverpageComponent = {
      ...coverpageComponent.content,
      data: {
        ...coverpageComponent.content.data,
        layout: newLayout,
        components,
      },
    };
    this.props.updateComponent(coverpageComponent, true, false, {
      ...component,
      oldCoverpageComponent,
    });
    this.props.nukeEditorState(true);
  };

  _updateComponentInCoverpage = (
    updatedSection,
    change,
    isMouseHover,
    oldSection,
    addedNewTextSection,
  ) => {
    if (!isMouseHover && oldSection && this.props.component) {
      let { component: coverpageComponent } = this.props;
      const oldCoverpageComponent = { ...coverpageComponent };

      coverpageComponent = {
        ...coverpageComponent.content,
        data: {
          ...coverpageComponent.content.data,
          components: {
            ...coverpageComponent.content.data.components,
            [updatedSection.id]: {
              ...coverpageComponent.content.data.components[updatedSection.id],
              content: updatedSection,
            },
          },
        },
      };
      this.props.updateComponent(
        coverpageComponent,
        change,
        isMouseHover,
        { ...oldSection, oldCoverpageComponent },
        addedNewTextSection,
      );
    }
  };

  _handleComponentLockUnlock = (section, lockStatus) => {
    if (section && this.props.component) {
      const toggleComponentLockCommand = new ToggleComponentLockCommand({
        toggleComponentLockUnlock: this.props.toggleComponentLockUnlock,
        payload: {
          lockStatus,
          activeComponentID: section.id,
          activeComponentType: section.type,
          oldSection: this.props.component.content,
          isCoverPageView: true,
          coverPageComponentId: this.props.componentId,
        },
      });
      this.props.executeCommand(toggleComponentLockCommand);
    }
  };

  _handleChangeThemeButton = coverPageType => {
    if (!this.props.isOpen) {
      this.props.updateToolPaneData(true);
    }
    this.props.coverpageThemeAction(coverPageType);
    this.props.changeToolsView("doc-components");
  };

  _changeThemeButton = coverpage => {
    if (!coverpage.lock) {
      return (
        <div className="change-coverpage-theme">
          <span
            className="revv-btn revv-btn-secondary-sm"
            role="button"
            tabIndex={0}
            onClick={() => {
              this._handleChangeThemeButton("OpenThemePane");
            }}
            onKeyPress={() => {}}>
            Select cover theme
          </span>
        </div>
      );
    }
    return null;
  };

  _customizeThemeButton = coverpage => {
    if (!coverpage.lock) {
      return (
        <div className="change-coverpage-theme">
          <span
            className="revv-btn revv-btn-secondary-sm"
            role="button"
            tabIndex={0}
            onClick={() => {
              this._handleChangeThemeButton("OpenCustomizationPane");
            }}
            onKeyPress={() => {}}>
            Customize cover theme
          </span>
        </div>
      );
    }
    return null;
  };

  _handleCoverpageLockUnlock = (event, flag) => {
    event.preventDefault();
    this.props.toggleBlockLockUnlock(this.props.component, flag);
  };

  // Lock Feature
  _lockButton = () => {
    const { clauseID } = this.props.component;

    let lockView = null;
    // for document, show only locked section because of added clause
    if (this.props.mode === DOCUMENT_MODE.DOCUMENT) {
      lockView = clauseID ? (
        <Tooltip
          placement="left"
          overlay="This clause is locked for any editing.">
          <span className="btn-icon locked-icon">
            <FontAwesomeIcon icon={["far", "lock"]} />
          </span>
        </Tooltip>
      ) : null;
    } else if (COMPONENT_FEATURE[this.props.mode].lockSection) {
      lockView = (
        <ContentLock
          toggleSectionLock={this._handleCoverpageLockUnlock}
          toggleUpgradePopup={this.props.toggleUpgradePopup}
          section={this.props.component.content}
          {...this.props}
        />
      );
    }
    return lockView;
  };

  // delete feature
  _deleteButton = () =>
    !(
      !COMPONENT_FEATURE[this.props.mode].deleteSection ||
      this.props.component.content.lock === true
    ) ? (
      <Tooltip
        placement="top"
        overlay="Delete Cover Page"
        align={{ offset: [0, 3] }}>
        <span
          data-test="documentitems-delete-the-content-btn"
          className="btn-icon"
          id="delete-the-content-btn"
          onClick={() => this._deleteCoverpageComponent()}
          onKeyDown={() => {}}
          role="button"
          tabIndex={0}>
          <FontAwesomeIcon icon={["far", "trash-alt"]} />
        </span>
      </Tooltip>
    ) : null;

  _decideSectionActionBtns = coverpage => {
    const actionBtns = !this.props.isLocked ? (
      <div
        className="style-component-wrapper"
        data-test="documentitems-component-wrapper-template-component-doc-view">
        <div
          className="style-component-action"
          data-test="documentitems-component-action-template-component-doc-view">
          {this._changeThemeButton(coverpage)}
          {this._customizeThemeButton(coverpage)}
          {this._lockButton()}
          {this._deleteButton()}
        </div>
      </div>
    ) : null;
    return actionBtns;
  };

  _updateBackgroundAttr = coverpageSec => {
    const { color, background } = coverpageSec.data.attributes;
    if (color && background === "0404e2") {
      coverpageSec.data.attributes.background = "#fff";
      delete coverpageSec.data.attributes.color;
    }
    return coverpageSec;
  };

  _themeAndBackgroundImage = coverpage => {
    const coverpageSec = this._updateBackgroundAttr(coverpage);
    const {
      attributes: {
        backgroundImage,
        imageTransparency,
        imagePosition,
        background,
        colorTransparency,
        appliedTheme,
        color,
      } = {},
    } = coverpageSec.data;
    let backgroundURL = "";
    if (
      backgroundImage &&
      typeof backgroundImage === "string" &&
      (backgroundImage.startsWith("https") ||
        backgroundImage.startsWith("/static") ||
        backgroundImage.startsWith("data"))
    ) {
      backgroundURL = backgroundImage;
    } else if (backgroundImage) {
      backgroundURL = this.props.imageBaseUrl + backgroundImage;
    }
    const styles = {
      backgroundImage: `url("${backgroundURL}")`,
      opacity: imageTransparency,
      backgroundPosition: imagePosition,
      backgroundRepeat: "no-repeat",
    };
    const colorStyles = {
      backgroundColor: background,
      opacity: colorTransparency,
    };
    const borderStyles = {
      borderColor: background,
    };

    return (
      <div
        className={`styling-coverpage-wrapper ${
          appliedTheme ? appliedTheme.toLowerCase() : ""
        }`}
        style={borderStyles}>
        {backgroundImage ? (
          <div className="styling-coverpage" style={styles} />
        ) : null}
        {/* Blue background issue fix */}
        {color ? null : (
          <div className="bgcolor-coverpage" style={colorStyles} />
        )}
      </div>
    );
  };

  _getView = () => {
    let lockedStatus = false;
    if (this.props.component && this.props.component.content) {
      lockedStatus = this.props.component.content.lock;
    }
    let className = "custom-coverpage ";
    if (
      this.props.draggedSection &&
      !ADD_COMPONENT_ALLOWED.COVERPAGE.includes(this.props.draggedSection.type)
    ) {
      className += " disable-component";
    }

    if (this.props.draggedSection && (this.props.isLocked || lockedStatus)) {
      className += " disable-component";
    }

    const emptyComponent = emptyContainer();
    let decideComponents = null;
    let decideLayout = null;
    let emptyBlockConfig = null;
    // Get the "components" & "layout" as an emptyComponent in case of empty document
    if (this.props.components && Object.keys(this.props.components).length) {
      decideComponents = this.props.components;
    } else {
      emptyBlockConfig = {
        flag: true,
        type: "COVERPAGE",
      };
      decideComponents = emptyComponent
        ? {
            [emptyComponent.id]: {
              row: undefined,
              col: undefined,
              content: emptyComponent,
            },
          }
        : null;
    }
    if (
      Array.isArray(this.props.layout) &&
      this.props.layout[0] &&
      this.props.layout[0].length > 0
    ) {
      decideLayout = this.props.layout;
    } else {
      emptyBlockConfig = {
        flag: true,
        type: "COVERPAGE",
      };
      decideLayout = emptyComponent ? [[emptyComponent.id]] : null;
    }
    const componentMeta = {
      blockType: DOCUMENT_BLOCKS.COVERPAGE,
      itemType: DOCUMENT_BLOCKS.COVERPAGE,
      coverpageId: this.props.componentId,
    };

    return (
      <div className={className}>
        <DocumentBlocks
          layout={decideLayout}
          components={decideComponents}
          id={this.props.componentId}
          blockType={DOCUMENT_BLOCKS.COVERPAGE}
          itemType={DOCUMENT_COMPONENT_TYPES.COVERPAGE}
          componentMeta={componentMeta}
          mode={this.props.mode}
          isLocked={this.props.isLocked || lockedStatus}
          emptyBlockConfig={emptyBlockConfig}
          addComponent={this._addComponentInCoverpage}
          updateComponent={this._updateComponentInCoverpage}
          deleteComponent={this._deleteComponentInCoverpage}
          cloneSection={this._cloneComponentInCoverpage}
          removeHighlightSelection={this.props.removeHighlightSelection}
          updateHoveredSectionId={this.props.updateHoveredSectionId}
          removeActiveComponents={this.props.removeActiveComponents}
          showDocumentDiffView={this.props.showDocumentDiffView}
          deleteSection={this.props.deleteSection}
          isSharedDocument={this.props.isSharedDocument}
          isDocumentLocked={this.props.isDocumentLocked}
          updateTemplateDocSectionLockUnlock={
            this.props.updateTemplateDocSectionLockUnlock
          }
          hoveredActiveComponentId={this.props.hoveredActiveComponentId}
          handleComponentLockUnlock={this._handleComponentLockUnlock}
          // Ref related props
          customToolbarRef={this.props.customToolbarRef}
          setRefsByType={this.props.setRefsByType}
          // ------- Drag & Drop related props -------
          draggedSection={this.props.draggedSection}
          draggedPosition={this.props.draggedPosition}
          dragggedOverData={this.props.dragggedOverData}
          onPlaceholderDragEnter={this.props.onPlaceholderDragEnter}
          onPlaceholderDragLeave={this.props.onPlaceholderDragLeave}
          onPlaceholderDrop={this._onPlaceholderDrop}
          tableColorpickerDropdownRef={this.props.tableColorpickerDropdownRef}
          match={this.props.match}
        />
      </div>
    );
  };

  render = () => {
    if (this.props.componentId) {
      const { content: { data } = {} } = this.props.component;
      // if coverpage data has key attributes and that key has a key isOpen
      // which is there for only new coverpage and unavailable for old ones
      // for old coverpages, be default show then
      const showCoverpage =
        data && data.attributes && data.attributes.isOpen !== undefined
          ? data.attributes.isOpen
          : true;

      if (showCoverpage) {
        return (
          <div
            className={`style-component ${
              this.props.component.content.lock && !this.props.isLocked
                ? " locked-style-component "
                : ""
            }`}
            id={this.props.componentId}
            // onMouseOver={() => {
            //   this.setState({
            //     activeSectionType: DOCUMENT_COMPONENT_TYPES.COVERPAGE,
            //     deleteSectionType: DOCUMENT_COMPONENT_TYPES.COVERPAGE,
            //     activeID: coverpage.id,
            //   });
            // }}
            // onMouseOut={() => {
            //   this.setState({ activeSectionType: null });
            // }}
          >
            <div className="coverpageview-header-wrapper">
              <p className="coverpage-title">{this.props.title}</p>
              {this._decideSectionActionBtns(this.props.component.content)}
            </div>
            {this._themeAndBackgroundImage(this.props.component.content)}
            {this._getView()}
          </div>
        );
      }
    }
    if (!this.props.isLocked) {
      return (
        <div
          className="btn-cover-page"
          role="button"
          tabIndex={0}
          onMouseDown={() => this._addCoverpage()}>
          <i>
            <FontAwesomeIcon icon={["far", "plus"]} />
          </i>
          Cover page
        </div>
      );
    }
    return null;
  };
}

const mapStateToProps = (state, props) => {
  const { componentId } = props;
  const {
    components,
    document: { image_base_url: imageBaseUrl },
  } = state.DocumentStore;
  let coverpageLayout = [[]];
  let coverpageComponents = {};
  const coverpage = componentId && components[componentId];
  if (coverpage && coverpage.content && coverpage.content.data) {
    if (Array.isArray(coverpage.content.data.layout))
      coverpageLayout = JSON.parse(
        JSON.stringify(coverpage.content.data.layout),
      );
    coverpageComponents = coverpage.content.data.components;
  }
  return {
    layout: coverpageLayout,
    components: coverpageComponents,
    component: coverpage,
    imageBaseUrl,
  };
};

const mapDispatchToProps = {
  nukeEditorState,
  updateToolPaneData,
  updateLayoutInsideComponent,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CoverpageView);



// WEBPACK FOOTER //
// ./src/components/DocumentV2/CoverpageView/CoverpageView.js